import { useEffect, forwardRef } from "react";

// Dev-only lint guard: warns in the console (and outlines offenders) whenever a page
// introduces horizontal overflow — the classic sign of a flex/grid child missing min-w-0.
// No-op in production, so it never affects the live app's performance.
export function OverflowGuard() {
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    let raf;
    const check = () => {
      const de = document.documentElement;
      if (de.scrollWidth > de.clientWidth + 1) {
        const offenders = Array.from(document.querySelectorAll("body *"))
          .filter((el) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 && (r.right > de.clientWidth + 1 || r.left < -1);
          })
          .slice(0, 8);
        // eslint-disable-next-line no-console
        console.warn(
          `[OverflowGuard] Horizontal overflow detected (scrollWidth=${de.scrollWidth} > clientWidth=${de.clientWidth}). ` +
            `A flex/grid child is likely missing min-w-0 (use <PageGrid>/<PageContainer> or add min-w-0). Offenders:`,
          offenders
        );
        offenders.forEach((el) => (el.style.outline = "2px dashed #D4AF37"));
        setTimeout(() => offenders.forEach((el) => (el.style.outline = "")), 4000);
      }
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };
    schedule();
    window.addEventListener("resize", schedule);
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", schedule);
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);
  return null;
}

const COL_MAP = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};
const GAP_MAP = { 3: "gap-3", 4: "gap-4", 5: "gap-5", 6: "gap-6", 8: "gap-8" };

// Responsive-by-default grid. Columns are responsive and min-w-0 is baked in so children
// can never blow out the layout. Prefer this over hand-rolled grid classes on new pages.
export const PageGrid = forwardRef(
  ({ cols = 1, gap = 6, className = "", children, ...rest }, ref) => (
    <div
      ref={ref}
      className={`grid ${COL_MAP[cols] || COL_MAP[1]} ${GAP_MAP[gap] || GAP_MAP[6]} min-w-0 ${className}`}
      {...rest}
    >
      {children}
    </div>
  )
);
PageGrid.displayName = "PageGrid";

// Standard page/section wrapper that stays within its bounds and never overflows.
export const PageContainer = ({ className = "", children, ...rest }) => (
  <div className={`w-full min-w-0 max-w-full ${className}`} {...rest}>
    {children}
  </div>
);
